import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CommonTable from '../../../Components/CommonTable';
import CommonPagination from '../../../Components/CommonPagination';
import NoDataPage from '../../../Components/NoDataPage';
import CommonLoader from '../../../Components/CommonLoader';
import ModuleIcon from '../../../Components/ModuleIcon';
import PageHeading from '../../../Components/PageHeading';
import AddOnCard from '../../../Dashboard/AddOns/AddOnCard';
import { useToast } from '../../../Components/CommonToast';
import RiskHeatmap from '../components/RiskHeatmap';
import { fontSize, SubHeading, Body } from '../../../Constants/Fonts';
import { risk_portal_risks } from '../../../services';
import { useRbac } from '../rbac';
import { currentUserEmail, visibleToMe } from '../workflow';

// RiskOwnerDashboard — the personal "risk owner" home. A focused, portfolio-style
// view of the risks the caller owns: where their exposure sits today (severity
// distribution + tolerance line), four headline KPIs, a residual-position
// heatmap (same house style as the Risk Manager dashboard) that filters the
// register on click, a "waiting on you" action list, and the owner's register
// with quick filter pills.
//
// Data: this screen fetches the FULL risk objects itself (risk_portal_risks with
// owner=<email>, scoped client-side via visibleToMe — same pattern as My Risks).
// The dashboard summary's `my_risks` omits scoring/likelihood/impact/strategy, so
// deriving the heatmap + severity split from it isn't possible; the full list
// carries everything. `overdueActions` still comes from the dashboard summary.

// Residual score above which a risk is "outside appetite" / above the tolerance
// line. Mirrors the mid-High boundary used across the portal.
const TOLERANCE = 12;

const extractItem = (res) =>
    res?.data?.response_data?.data || res?.data?.data ||
    res?.statusCode?.response_data?.data || res?.statusCode?.data || null;

// ISO 31000 5×5 rating from a score (matches the portal's ratingOf5x5).
const ratingFromScore = (s) => (s >= 16 ? 'critical' : s >= 10 ? 'high' : s >= 5 ? 'medium' : 'low');

// Rating → severity colour + soft tint tokens (same palette as the register).
const RATING = {
    critical: { color: 'var(--color-critical)', soft: 'var(--color-critical-soft)' },
    high: { color: 'var(--color-high)', soft: 'var(--color-high-soft)' },
    medium: { color: 'var(--color-medium)', soft: 'var(--color-medium-soft)' },
    low: { color: 'var(--color-low)', soft: 'var(--color-low-soft)' },
};
const ratingToken = (r) => RATING[String(r || '').toLowerCase()] || { color: 'var(--color-text-secondary)', soft: 'var(--color-surface-muted)' };

// Heatmap-matched severity palette — the exact hues from the Residual position
// grid (COLOR_MATRIX in RiskHeatmap.js) so the exposure bar reads consistently
// with the heatmap. `color` = solid hue, `soft` = low-opacity tint for the bar
// segments / legend backgrounds.
const HEAT_SEV = {
    critical: { color: '#D24646', soft: 'rgba(210, 70, 70, 0.14)' },
    high: { color: '#DE9E30', soft: 'rgba(222, 158, 48, 0.14)' },
    medium: { color: '#D9BF21', soft: 'rgba(217, 191, 33, 0.14)' },
    low: { color: '#19A07E', soft: 'rgba(25, 160, 126, 0.14)' },
};
const heatToken = (k) => HEAT_SEV[k] || { color: 'var(--color-text-secondary)', soft: 'var(--color-surface-muted)' };

// Severity buckets for the distribution bar + legend (order = bar order).
const SEV_BUCKETS = [
    { key: 'critical', label: 'Critical', range: 'score 16–25' },
    { key: 'high', label: 'High', range: '10–15' },
    { key: 'medium', label: 'Medium', range: '5–9' },
    { key: 'low', label: 'Low', range: '1–4' },
];

const titleCase = (s) => (s ? s[0].toUpperCase() + s.slice(1) : '—');

const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
};

// Whole days from today to `iso` (negative = overdue).
const daysUntil = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return Math.round((d - t) / 86400000);
};

// Normalise a full risk object into the residual-position shape this view uses.
// `score`/`rating`/`likelihood`/`impact` reflect the RESIDUAL position when the
// risk has been re-assessed, falling back to inherent otherwise.
const normRisk = (r = {}) => {
    const inherentScore = Number(r.inherent_score) || (Number(r.likelihood) * Number(r.impact)) || 0;
    const residualScore = Number(r.residual_score) || (Number(r.residual_likelihood) * Number(r.residual_impact)) || 0;
    const assessed = r.assessed ?? residualScore > 0;
    const score = residualScore || inherentScore;
    const rating = String((residualScore ? r.residual_rating : r.inherent_rating) || ratingFromScore(score)).toLowerCase();
    return {
        id: r.id ?? r.risk_registry_id ?? r.pk,
        risk_id: r.risk_id,
        risk: r.risk || '',
        type: String(r.risk_type || '').replace(/_/g, ' '),
        score,
        rating,
        inherentScore,
        residualScore,
        assessed,
        scored: score > 0,
        likelihood: Number(r.residual_likelihood || r.likelihood) || 0,
        impact: Number(r.residual_impact || r.impact) || 0,
        strategy: r.strategy || '',
        state: r.state || '',
        review: r.next_review_date || r.review_date || r.target_date || '',
    };
};

// Soft-tinted residual badge — "Critical 20".
const residualBadge = (r) => {
    const { color, soft } = ratingToken(r.rating);
    return (
        <Body
            as="span"
            weight="semibold"
            style={{
                display: 'inline-block', padding: '3px 10px', borderRadius: 'var(--radius-pill)',
                background: soft, color, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
            }}
        >
            {r.scored ? `${titleCase(r.rating)} ${r.score}` : 'Not scored'}
        </Body>
    );
};

// Movement = residual vs inherent (did treatment move the number?). Honest signal
// from the two scores we hold — not a time series.
const movement = (r) => {
    if (!r.assessed || !r.inherentScore) return { arrow: '—', label: 'stable', color: 'var(--color-text-muted)' };
    if (r.residualScore < r.inherentScore) return { arrow: '↓', label: 'falling', color: 'var(--color-success)' };
    if (r.residualScore > r.inherentScore) return { arrow: '↑', label: 'rising', color: 'var(--color-danger)' };
    return { arrow: '—', label: 'stable', color: 'var(--color-text-muted)' };
};

// Card header: tinted icon chip + title (+ optional subtitle), optional
// right-side action text.
const CardHead = ({ icon, title, subtitle, action }) => (
    <div className="rp1_dash_card_head">
        <div className="rp1_dash_card_headleft">
            <span className="rp1_dash_head_icon"><ModuleIcon name={icon} size={18} /></span>
            <div className="rp1_dash_card_titles">
                <SubHeading variant={2} color="var(--color-text-primary)" style={{ margin: 0 }}>{title}</SubHeading>
                {subtitle && <Body variant={2} color="var(--color-text-muted)" style={{ margin: 0 }}>{subtitle}</Body>}
            </div>
        </div>
        {action}
    </div>
);

const PAGE_SIZE = 8;
const HEADERS = ['Risk', 'Description', 'Residual', 'Movement', 'Treatment', 'Next review'];

export default function RiskOwnerDashboard({ overdueActions = 0, onOpenRisk }) {
    const { role, stages } = useRbac();
    const email = useMemo(() => currentUserEmail(), []);
    const { notify, toastNode } = useToast();

    const [risks, setRisks] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: 1, page_size: 500 };
            if (email) params.owner = email;
            const d = extractItem(await risk_portal_risks(params));
            const list = (d && Array.isArray(d.results)) ? d.results : [];
            setRisks(list.filter((r) => visibleToMe(r, { role, email, stages })).map(normRisk));
        } catch (e) {
            console.error('Load owner dashboard failed', e);
            setRisks([]);
            notify('Failed to load your risks', 'error');
        } finally {
            setLoading(false);
        }
    }, [role, email, stages, notify]);

    useEffect(() => { load(); }, [load]);

    const total = risks.length;

    // ── Derived exposure figures (assessed risks only for the severity split) ─
    const sevCounts = useMemo(() => {
        const c = { critical: 0, high: 0, medium: 0, low: 0 };
        risks.forEach((r) => { if (r.scored && r.rating in c) c[r.rating] += 1; });
        return c;
    }, [risks]);
    const sevSum = SEV_BUCKETS.reduce((a, s) => a + sevCounts[s.key], 0);

    const aboveTolerance = useMemo(() => risks.filter((r) => (r.score || 0) > TOLERANCE).length, [risks]);
    const reviewsDueSoon = useMemo(
        () => risks.filter((r) => { const d = daysUntil(r.review); return d != null && d >= 0 && d <= 14; }),
        [risks],
    );
    const inProgressCount = useMemo(() => risks.filter((r) => r.state === 'in_progress').length, [risks]);

    // Residual-position matrix — matrix[likelihood-1][impact-1] = count.
    const residualMatrix = useMemo(() => {
        const m = Array.from({ length: 5 }, () => Array(5).fill(0));
        risks.forEach((r) => {
            const L = Math.round(r.likelihood); const I = Math.round(r.impact);
            if (L >= 1 && L <= 5 && I >= 1 && I <= 5) m[L - 1][I - 1] += 1;
        });
        return m;
    }, [risks]);
    const hasAssessed = useMemo(() => risks.some((r) => r.likelihood >= 1 && r.impact >= 1), [risks]);

    // "Waiting on you" — the owner's risks by due-date urgency (overdue first).
    const tasks = useMemo(() => (
        risks
            .map((r) => ({ r, d: daysUntil(r.review) }))
            .filter((t) => t.d != null && t.d <= 21)
            .sort((a, b) => a.d - b.d)
            .slice(0, 6)
    ), [risks]);

    // ── KPI tiles — rendered via the shared AddOnCard (tinted icon chip, label +
    //    sub, big value in the trailing action slot). ─────────────────────────
    const KPIS = [
        { label: 'Outside appetite', value: aboveTolerance, sub: `${sevCounts.critical} critical`, color: 'var(--color-critical)', bg: 'var(--color-critical-soft)', icon: 'violation' },
        { label: 'Actions overdue', value: overdueActions, sub: overdueActions ? 'Needs your attention' : 'All on track', color: 'var(--color-high)', bg: 'var(--color-high-soft)', icon: 'risk' },
        { label: 'Reviews due in 14 days', value: reviewsDueSoon.length, sub: reviewsDueSoon.slice(0, 3).map((r) => r.risk_id).filter(Boolean).join(', ') || 'None scheduled', color: 'var(--color-primary)', bg: 'var(--color-primary-soft)', icon: 'score' },
        { label: 'Assigned to you', value: total, sub: `${inProgressCount} in progress`, color: 'var(--color-primary)', bg: 'var(--color-primary-soft)', icon: 'coverage' },
    ];

    // ── Register: pill filters + heatmap-cell filter ────────────────────────
    const [filter, setFilter] = useState('all');
    const [activeCell, setActiveCell] = useState(null); // "L,I"
    const [page, setPage] = useState(0);

    const PILLS = [
        { id: 'all', label: `All ${total}` },
        { id: 'appetite', label: 'Outside appetite' },
        { id: 'review', label: 'Review due soon' },
        { id: 'severe', label: 'High & above' },
    ];

    const filtered = useMemo(() => {
        let rows = risks;
        if (filter === 'appetite') rows = rows.filter((r) => (r.score || 0) > TOLERANCE);
        else if (filter === 'review') rows = rows.filter((r) => { const d = daysUntil(r.review); return d != null && d >= 0 && d <= 14; });
        else if (filter === 'severe') rows = rows.filter((r) => ['critical', 'high'].includes(r.rating));
        if (activeCell) {
            const [L, I] = activeCell.split(',').map(Number);
            rows = rows.filter((r) => Math.round(r.likelihood) === L && Math.round(r.impact) === I);
        }
        return rows;
    }, [risks, filter, activeCell]);

    const pageCount = Math.ceil(filtered.length / PAGE_SIZE) || 1;
    useEffect(() => { if (page > pageCount - 1) setPage(0); }, [page, pageCount]);
    const pageItems = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    const onCellClick = (likVal, impactVal) => {
        const key = `${likVal},${impactVal}`;
        setActiveCell((cur) => (cur === key ? null : key));
        setPage(0);
    };
    const selectPill = (id) => { setFilter(id); setActiveCell(null); setPage(0); };

    const rows = pageItems.map((r) => {
        const mv = movement(r);
        const outside = (r.score || 0) > TOLERANCE;
        return {
            data: [
                <Body as="span" color="var(--color-primary)" weight="semibold">{r.risk_id || '—'}</Body>,
                <span className="rp1_owner_desc">
                    <Body as="span" color="var(--color-text-primary)" weight="medium" className="rp1_legend_ellipsis" style={{ margin: 0, display: 'block' }}>
                        {r.risk || '—'}
                    </Body>
                    <Body as="span" variant={2} color="var(--color-text-muted)" style={{ margin: 0 }}>
                        {r.type ? titleCase(r.type) : 'Uncategorised'}
                        {outside && <Body as="span" variant={2} weight="semibold" color="var(--color-danger)" style={{ marginLeft: 6 }}>· outside appetite</Body>}
                    </Body>
                </span>,
                residualBadge(r),
                <Body as="span" weight="semibold" color={mv.color} style={{ whiteSpace: 'nowrap' }}>{mv.arrow} {mv.label}</Body>,
                <Body as="span" color="var(--color-text-secondary)">{r.strategy ? titleCase(r.strategy) : '—'}</Body>,
                <Body as="span" color="var(--color-text-secondary)" style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.review)}</Body>,
            ],
        };
    });

    return (
        <div className="rp1_owner_home">
            {/* 0 ── Page heading — the owner dashboard's page-level title ──────── */}
            <div className='cmn_elevated_card p-3'>
                <PageHeading
                    title="Risk Owner Dashboard"
                    subtitle="Your personal view of the risks you own — exposure, actions and upcoming reviews."
                />

                {/* 1 ── Exposure banner: severity distribution + tolerance line ───── */}
                <div className="cmn_elevated_card p-3 rp1_owner_banner">
                    {loading && <CommonLoader overlay label="Loading your risks…" />}
                    <CardHead
                        icon="score"
                        title="Where your exposure sits today"
                        subtitle="Risk volume by severity level."
                        action={(
                            <Body as="span" variant={2} color="var(--color-text-muted)" style={{ margin: 0 }}>
                                {aboveTolerance} of {total} risks sit above your tolerance line of {TOLERANCE}
                            </Body>
                        )}
                    />

                    {sevSum > 0 ? (
                        <>
                            <div className="rp1_owner_sevbar">
                                {SEV_BUCKETS.map((s) => {
                                    const n = sevCounts[s.key];
                                    if (!n) return null;
                                    return (
                                        <div
                                            key={s.key}
                                            className="rp1_owner_sevseg"
                                            style={{ flexGrow: n, background: heatToken(s.key).color }}
                                            title={`${s.label}: ${n}`}
                                        >
                                            {n}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="rp1_owner_sevlegend">
                                {SEV_BUCKETS.map((s) => (
                                    <span key={s.key} className="rp1_owner_sevkey">
                                        <span className="rp1_owner_sevdot" style={{ background: heatToken(s.key).color }} />
                                        <Body as="span" variant={2} color="var(--color-text-secondary)">
                                            {s.label}, {s.range}
                                        </Body>
                                    </span>
                                ))}
                            </div>
                        </>
                    ) : (
                        <NoDataPage hint="Your severity distribution appears once your risks are scored." />
                    )}
                </div>

                {/* 2 ── KPI strip — shared AddOnCard tiles ────────────────────────── */}
                <div className="rp1_stat_row rp1_stat_row--4 mt-4">
                    {KPIS.map((k) => (
                        <AddOnCard
                            key={k.label}
                            className="cmn_elevated_card"
                            // boxShadow:undefined lets the .cmn_elevated_card class shadow win.
                            style={{ boxShadow: undefined, height: '100%' }}
                            align="flex-start"
                            icon={k.icon}
                            iconColor={k.color}
                            iconBg={k.bg}
                            label={(
                                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                                    <Body as="span" weight="semibold" color="var(--color-text-primary)" style={{ margin: 0 }}>{k.label}</Body>
                                    <Body as="span" variant={2} color="var(--color-text-muted)" className="rp1_legend_ellipsis" style={{ margin: 0 }}>{k.sub}</Body>
                                </span>
                            )}
                            action={(
                                <Body as="span" weight="bold" style={{ margin: 0, fontFamily: 'var(--font-family)', fontSize: 26, color: k.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                    {k.value}
                                </Body>
                            )}
                        />
                    ))}
                </div>
            </div>

            {/* 3 ── Residual position (heatmap) + Waiting on you ──────────────── */}
            <div className="cmn_elevated_card  p-3 rp1_dash_split">
                <div className="rp1_dash_card cmn_elevated_card">
                    <CardHead
                        icon="score"
                        title="Risk Heat Map"
                        action={(
                            <Body as="span" variant={2} color={activeCell ? 'var(--color-primary)' : 'var(--color-text-muted)'} style={{ cursor: activeCell ? 'pointer' : 'default' }} onClick={activeCell ? () => setActiveCell(null) : undefined}>
                                {activeCell ? 'Clear cell filter ✕' : 'Select a cell to filter'}
                            </Body>
                        )}
                    />
                    {hasAssessed ? (
                        <RiskHeatmap matrix={residualMatrix} activeCell={activeCell} onCellClick={onCellClick} />
                    ) : (
                        <NoDataPage hint="The residual heatmap populates once your risks are assessed." />
                    )}
                </div>

                <div className="rp1_dash_card cmn_elevated_card">
                    <CardHead
                        icon="check_circle"
                        title="Waiting on you"
                        action={<Body as="span" variant={2} color="var(--color-text-muted)">This week</Body>}
                    />
                    {tasks.length ? (
                        <div className="rp1_owner_tasks">
                            {tasks.map(({ r, d }) => {
                                const overdue = d < 0;
                                return (
                                    <div key={r.id || r.risk_id} className="rp1_owner_task" onClick={() => onOpenRisk?.(r.id)}>
                                        <span className="rp1_owner_task_check" aria-hidden="true" />
                                        <span className="rp1_owner_task_text">
                                            <Body as="span" color="var(--color-text-primary)" weight="medium" className="rp1_legend_ellipsis" style={{ margin: 0, display: 'block' }}>
                                                {r.risk || 'Untitled risk'}
                                            </Body>
                                            <Body as="span" variant={2} style={{ margin: 0 }} color="var(--color-text-muted)">
                                                {r.risk_id || '—'} —{' '}
                                                <Body as="span" variant={2} weight="semibold" color={overdue ? 'var(--color-danger)' : 'var(--color-text-secondary)'}>
                                                    {overdue ? `Overdue by ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'}` : d === 0 ? 'Due today' : `Due in ${d} day${d === 1 ? '' : 's'}`}
                                                </Body>
                                            </Body>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <NoDataPage hint="Nothing needs your attention this week." />
                    )}
                </div>
            </div>

            {/* 4 ── Your register ─────────────────────────────────────────────── */}
            <div className="cmn_elevated_card p-3 rp1_owner_register">
                <PageHeading
                    title="Risk register"
                    actions={(
                        <div className="rp1_owner_pills">
                            {PILLS.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    className={`rp1_owner_pill${filter === p.id ? ' rp1_owner_pill--active' : ''}`}
                                    onClick={() => selectPill(p.id)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    )}
                />

                {rows.length === 0 ? (
                    <NoDataPage
                        title={total === 0 ? 'No risks assigned to you' : 'Nothing matches this filter'}
                        hint={total === 0 ? "When a Risk Manager assigns you as owner of a risk, it'll appear here." : 'Try a different filter or clear the selected heatmap cell.'}
                    />
                ) : (
                    <CommonTable
                        headers={HEADERS}
                        rows={rows}
                        noScroll
                        onRowClick={(_row, i) => onOpenRisk?.(pageItems[i].id)}
                    />
                )}

                {pageCount > 1 && (
                    <div className="rp1_register_pagination">
                        <Body as="span" variant={2} weight="medium" style={{ fontSize: fontSize.sm, color: 'var(--color-text-muted)' }}>
                            Page {page + 1} of {pageCount} ({filtered.length} shown)
                        </Body>
                        <CommonPagination
                            pageCount={pageCount}
                            forcePage={page}
                            handlePageClick={({ selected }) => setPage(selected)}
                        />
                    </div>
                )}
            </div>

            {toastNode}
        </div>
    );
}
